import {
  FUNNEL_EVENTS,
  buildFileEventParams,
  trackFunnelEvent
} from './gaFunnelEvents.js'
import { normalizeInt64IdOrNull } from './int64Id.js'

export const UPLOAD_STATUS = Object.freeze({
  IDLE: 'idle',
  UPLOADING: 'uploading',
  UPLOADED: 'uploaded',
  SCANNED: 'scanned',
  FAILED: 'failed'
})

function normalizeUploadPages(pages) {
  if (pages === null || pages === undefined) {
    return undefined
  }
  const parsed = Number(pages)
  if (!Number.isFinite(parsed)) {
    return undefined
  }
  return parsed
}

function normalizeUploadedFileId(fileId) {
  return normalizeInt64IdOrNull(fileId)
}

function createInitialConfirmDialog() {
  return {
    visible: false,
    fileName: '',
    pages: undefined,
    estimatedPages: undefined,
    sourceLanguage: '',
    targetLanguage: ''
  }
}

function normalizeFileName(file) {
  if (!file || typeof file.name !== 'string') {
    return ''
  }
  return file.name
}

function resolveTotalPages(limitInfo) {
  if (!limitInfo || typeof limitInfo !== 'object') {
    return 0
  }
  const total = Number(limitInfo.totalPages || 0)
  if (!Number.isFinite(total)) {
    return 0
  }
  return total
}

export function createAutoUploadConfirmFlow(deps) {
  const uploadFile = deps?.uploadFile
  const getLimitInfo = deps?.getLimitInfo
  const createTaskWithFileId = deps?.createTaskWithFileId

  if (typeof uploadFile !== 'function') {
    throw new Error('createAutoUploadConfirmFlow requires uploadFile function')
  }
  if (typeof getLimitInfo !== 'function') {
    throw new Error('createAutoUploadConfirmFlow requires getLimitInfo function')
  }
  if (typeof createTaskWithFileId !== 'function') {
    throw new Error('createAutoUploadConfirmFlow requires createTaskWithFileId function')
  }

  const trackEvent = typeof deps.trackEvent === 'function' ? deps.trackEvent : trackFunnelEvent
  const buildEventParams = typeof deps.buildEventParams === 'function'
    ? deps.buildEventParams
    : buildFileEventParams

  const initialState = {
    selectedFile: null,
    uploadStatus: UPLOAD_STATUS.IDLE,
    uploadSessionId: 0,
    uploadedFileId: null,
    uploadedFileName: '',
    uploadedPages: undefined,
    confirmDialog: createInitialConfirmDialog(),
    isSubmitting: false
  }
  const state = typeof deps?.createState === 'function'
    ? deps.createState(initialState)
    : initialState

  const resetUploadedContext = () => {
    state.uploadStatus = UPLOAD_STATUS.IDLE
    state.uploadedFileId = null
    state.uploadedFileName = ''
    state.uploadedPages = undefined
    state.confirmDialog = createInitialConfirmDialog()
  }

  const setSelectedFile = (file) => {
    state.selectedFile = file ?? null
    // 文件被替换时，需要令旧会话结果失效。
    state.uploadSessionId += 1
    resetUploadedContext()
  }

  const clearSelectedFile = () => {
    setSelectedFile(null)
  }

  const isSessionActive = (sessionId) => {
    return sessionId === state.uploadSessionId
  }

  const openConfirmDialog = ({ fileName, pages, sourceLanguage, targetLanguage }) => {
    state.confirmDialog = {
      visible: true,
      fileName,
      pages,
      estimatedPages: pages,
      sourceLanguage: sourceLanguage || '',
      targetLanguage: targetLanguage || ''
    }
  }

  const autoUploadCurrentFile = async ({ sourceLanguage, targetLanguage } = {}) => {
    const file = state.selectedFile
    if (!file) {
      return { ok: false, reason: 'no_file' }
    }

    const currentSessionId = state.uploadSessionId + 1
    state.uploadSessionId = currentSessionId
    state.uploadStatus = UPLOAD_STATUS.UPLOADING

    const fileName = normalizeFileName(file)
    trackEvent(FUNNEL_EVENTS.UPLOAD_START, buildEventParams(fileName))

    try {
      const uploadResult = await uploadFile(file)
      if (!isSessionActive(currentSessionId)) {
        return { ok: false, reason: 'stale_upload_result' }
      }

      // 扫描版PDF拦截
      if (uploadResult?.isScanned === true) {
        state.uploadStatus = UPLOAD_STATUS.SCANNED
        state.uploadedFileId = null
        state.uploadedFileName = ''
        state.uploadedPages = undefined
        state.confirmDialog = createInitialConfirmDialog()
        return {
          ok: true,
          reason: 'scanned_pdf',
          uploadSessionId: currentSessionId
        }
      }

      const normalizedFileId = normalizeUploadedFileId(uploadResult?.fileId)
      if (!normalizedFileId) {
        state.uploadStatus = UPLOAD_STATUS.FAILED
        state.uploadedFileId = null
        state.uploadedFileName = ''
        state.uploadedPages = undefined
        state.confirmDialog = createInitialConfirmDialog()
        return { ok: false, reason: 'invalid_file_id' }
      }

      const normalizedPages = normalizeUploadPages(uploadResult?.pages)
      state.uploadStatus = UPLOAD_STATUS.UPLOADED
      state.uploadedFileId = normalizedFileId
      state.uploadedFileName = fileName
      state.uploadedPages = normalizedPages
      openConfirmDialog({
        fileName,
        pages: normalizedPages,
        sourceLanguage,
        targetLanguage
      })

      trackEvent(
        FUNNEL_EVENTS.UPLOAD_SUCCESS,
        buildEventParams(fileName, { pages: normalizedPages })
      )

      return {
        ok: true,
        reason: 'uploaded',
        uploadSessionId: currentSessionId,
        uploadedFileId: state.uploadedFileId,
        uploadedPages: state.uploadedPages
      }
    } catch (error) {
      if (!isSessionActive(currentSessionId)) {
        return { ok: false, reason: 'stale_upload_result' }
      }

      state.uploadStatus = UPLOAD_STATUS.FAILED
      return {
        ok: false,
        reason: 'upload_failed',
        error
      }
    }
  }

  const selectFileAndUpload = async (file, options = {}) => {
    setSelectedFile(file)
    return autoUploadCurrentFile(options)
  }

  const retryUpload = async (options = {}) => {
    if (!state.selectedFile) {
      return { ok: false, reason: 'no_file' }
    }
    return autoUploadCurrentFile(options)
  }

  const setConfirmDialogLanguages = ({ sourceLanguage, targetLanguage }) => {
    if (typeof sourceLanguage === 'string') {
      state.confirmDialog.sourceLanguage = sourceLanguage
    }
    if (typeof targetLanguage === 'string') {
      state.confirmDialog.targetLanguage = targetLanguage
    }
  }

  const cancelConfirm = () => {
    state.confirmDialog.visible = false
  }

  const startTranslation = async ({ sourceLanguage, targetLanguage, category = 'general' } = {}) => {
    if (!state.uploadedFileId || !state.uploadedFileName) {
      return { ok: false, reason: 'file_not_ready' }
    }

    state.isSubmitting = true

    try {
      let limitInfo
      try {
        limitInfo = await getLimitInfo()
      } catch (error) {
        return { ok: false, reason: 'quota_failed', error }
      }

      const totalPages = resolveTotalPages(limitInfo)
      if (totalPages <= 0) {
        trackEvent(
          FUNNEL_EVENTS.QUOTA_BLOCK,
          buildEventParams(state.uploadedFileName)
        )
        return { ok: false, reason: 'quota_block' }
      }

      trackEvent(
        FUNNEL_EVENTS.TRANSLATION_START,
        buildEventParams(state.uploadedFileName)
      )

      try {
        const taskId = await createTaskWithFileId(
          state.uploadedFileId,
          state.uploadedFileName,
          targetLanguage || '',
          sourceLanguage || 'auto',
          category
        )

        return { ok: true, reason: 'task_created', taskId }
      } catch (error) {
        return { ok: false, reason: 'create_failed', error }
      }
    } finally {
      state.isSubmitting = false
    }
  }

  const confirmAndStartTranslation = async ({ sourceLanguage, targetLanguage, category = 'general' } = {}) => {
    setConfirmDialogLanguages({ sourceLanguage, targetLanguage })
    state.confirmDialog.visible = false

    return startTranslation({
      sourceLanguage: state.confirmDialog.sourceLanguage,
      targetLanguage: state.confirmDialog.targetLanguage,
      category
    })
  }

  return {
    state,
    setSelectedFile,
    clearSelectedFile,
    selectFileAndUpload,
    retryUpload,
    cancelConfirm,
    setConfirmDialogLanguages,
    startTranslation,
    confirmAndStartTranslation
  }
}

export function __testOnly__normalizeUploadPages(value) {
  return normalizeUploadPages(value)
}
